import React from "react";
import PropTypes from "kf-proptypes/widgets";

import { FieldLabel, FieldHint, FieldErrors } from "../fields/index.jsx";
import {
  CURRENCY_SYMBOLS,
  CURRENCY_STYLE_TYPES,
  CURRENCY_SIZES
} from "./constants.js";
import { ReadonlyCurrency } from "./readonly.js";
import { EditableCurrency } from "./editable.js";

export {
  CURRENCY_SYMBOLS as CurrencySymbols,
  CURRENCY_SIZES,
  CURRENCY_STYLE_TYPES as CurrencyStyleType
};
export function Currency({
  value,
  handleBlur,
  decimalPoint,
  readOnly,
  className,
  size,
  currencyTypes = [],
  isError,
  onChange,
  onKeyDown,
  onFocus,
  styleType,
  autoComplete,
  id,
  label,
  hint,
  errors,
  required,
  disabled,
  labelLeftIcon,
  labelRightIcon
}) {
  return (
    <React.Fragment>
      {label && (
        <FieldLabel
          label={label}
          required={required}
          targetId={id}
          disabled={disabled}
          leftIcon={labelLeftIcon}
          rightIcon={labelRightIcon}
          size={size}
        />
      )}
      {readOnly ? (
        <ReadonlyCurrency
          value={value}
          decimalPoint={decimalPoint}
          className={className}
          size={size}
          currencyTypes={currencyTypes}
          isError={isError}
          styleType={styleType}
          errors={errors}
          disabled={disabled}
          id={id}
        />
      ) : (
        <EditableCurrency
          value={value}
          decimalPoint={decimalPoint}
          className={className}
          size={size}
          currencyTypes={currencyTypes}
          isError={isError}
          styleType={styleType}
          autoComplete={autoComplete}
          errors={errors}
          disabled={disabled}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={handleBlur}
          id={id}
        />
      )}

      {hint && <FieldHint hint={hint} targetId={id} />}
      {errors && errors.length > 0 && (
        <FieldErrors errors={errors} targetId={id} />
      )}
    </React.Fragment>
  );
}

Currency.propTypes = {
  /** Current value of the currency field  */
  value: PropTypes.string.isRequired,
  /** Callback function to make change on blur */
  handleBlur: PropTypes.func,
  /**Specifies the number of decimal points to be displayed or allowed in the currency */
  decimalPoint: PropTypes.number,
  /** Callback function return the value of the currency field changes */
  onChange: PropTypes.func.isRequired,
  /** Callback function return when a key is pressed down */
  onKeyDown: PropTypes.func,
  /** Callback function that will be called when the currency field receives focus or to make focus*/
  onFocus: PropTypes.func,
  /** The className for custom style */
  className: PropTypes.string,
  /** Specifies whether the currency is read-only (true) or editable (false)  */
  readOnly: PropTypes.bool,
  /** Indicates whether there is an error */
  isError: PropTypes.bool,
  /** An array containing the available currency types or options for the user to select from */
  currencyTypes: PropTypes.oneOf(Object.keys(CURRENCY_SYMBOLS)).isRequired,
  /** Represents the size */
  size: PropTypes.oneOf(Object.values(CURRENCY_SIZES)),
  /** Specifies the style type  */
  styleType: PropTypes.oneOf(Object.values(CURRENCY_STYLE_TYPES)),
  /** A string specifying the auto-complete behavior  */
  autoComplete: PropTypes.string,
  /** Unique identifier for targeting the feild  */
  id: PropTypes.string,
  /** Representing the title */
  label: PropTypes.string,
  /** Providing a small message or hint */
  hint: PropTypes.string,
  /** Array of error that displayed in currency */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** Make currency is mandotory to be filled */
  required: PropTypes.bool,
  /** Currency goes disabled state  */
  disabled: PropTypes.bool,
  /** it will render left icon of label field */
  labelLeftIcon: PropTypes.node,
  /** it will render right icon of label field */
  labelRightIcon: PropTypes.node
};

Currency.defaultProps = {
  handleBlur: () => {},
  handleFocus: () => {},
  onChange: () => {},
  onKeyDown: () => {},
  onFocus: () => {},
  styleType: CURRENCY_STYLE_TYPES.NORMAL,
  size: CURRENCY_SIZES.BASE,
  currencyTypes: [],
  value: "",
  className: "",
  isError: false,
  errors: [],
  disabled: false
};
